/** AuditExpensePanel — merged Audit & Expenses tooling:
 * credited/debited totals, manual credit/debit entries, Excel upload
 * (predefined columns), and shareable audit links (duration → link → email/WhatsApp). */
import { useEffect, useRef, useState } from "react";
import {
  ArrowDownCircle, ArrowUpCircle, FileDown, FileSpreadsheet, Link2, Loader2,
  Mail, Plus, Send, Trash2, Upload, X, Copy,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { WaShareButton } from "@/components/WaShareButton";

const inr = (n) => `₹${Number(n || 0).toLocaleString()}`;

export function AuditExpensePanel({ records, onChanged }) {
  const credited = records.filter((r) => (r.entry_type || "credit") !== "debit").reduce((s, r) => s + Number(r.amount || 0), 0);
  const debited = records.filter((r) => (r.entry_type || "credit") === "debit").reduce((s, r) => s + Number(r.amount || 0), 0);
  return (
    <div className="space-y-4" data-testid="audit-expense-panel">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white p-4 ring-1 ring-emerald-200" data-testid="audit-credited-card">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700"><ArrowDownCircle size={12} /> Credited amount</div>
          <div className="mt-1 text-2xl font-bold text-emerald-700">{inr(credited)}</div>
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-rose-50 to-white p-4 ring-1 ring-rose-200" data-testid="audit-debited-card">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-rose-700"><ArrowUpCircle size={12} /> Debited amount</div>
          <div className="mt-1 text-2xl font-bold text-rose-700">{inr(debited)}</div>
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-slate-50 to-white p-4 ring-1 ring-slate-200" data-testid="audit-net-card">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Net</div>
          <div className={`mt-1 text-2xl font-bold ${credited - debited >= 0 ? "text-[#0F2042]" : "text-rose-700"}`}>{inr(credited - debited)}</div>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <ManualEntryCard onChanged={onChanged} />
        <ExcelUploadCard onChanged={onChanged} />
      </div>
      <AuditShareLinks />
    </div>
  );
}

function ManualEntryCard({ onChanged }) {
  const [form, setForm] = useState({ entry_type: "credit", amount: "", description: "", method: "", record_date: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const add = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post("/hr/audit/entries", { ...form, amount: Number(form.amount) });
      toast.success(`${form.entry_type === "debit" ? "Debited" : "Credited"} amount added to audit`);
      setForm({ entry_type: form.entry_type, amount: "", description: "", method: "", record_date: "" });
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="audit-entry-card">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F97316]">// add credit / debit entry</div>
      <div className="mt-2 flex rounded-full bg-slate-100 p-0.5 text-xs font-bold">
        <button onClick={() => set("entry_type", "credit")} data-testid="audit-entry-credit"
          className={`flex-1 rounded-full py-1.5 ${form.entry_type === "credit" ? "bg-emerald-600 text-white" : "text-slate-500"}`}>Credited amount</button>
        <button onClick={() => set("entry_type", "debit")} data-testid="audit-entry-debit"
          className={`flex-1 rounded-full py-1.5 ${form.entry_type === "debit" ? "bg-rose-600 text-white" : "text-slate-500"}`}>Debited amount</button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <input data-testid="audit-entry-amount" type="number" placeholder="Amount (INR) *" value={form.amount} onChange={(e) => set("amount", e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#F97316] focus:outline-none" />
        <input data-testid="audit-entry-date" type="date" value={form.record_date} onChange={(e) => set("record_date", e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#F97316] focus:outline-none" />
        <input data-testid="audit-entry-desc" placeholder="Description" value={form.description} onChange={(e) => set("description", e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#F97316] focus:outline-none" />
        <input data-testid="audit-entry-method" placeholder="Method (Bank / UPI / Cash)" value={form.method} onChange={(e) => set("method", e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#F97316] focus:outline-none" />
      </div>
      <button onClick={add} disabled={busy} data-testid="audit-entry-add"
        className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[#0F2042] py-2 text-xs font-bold text-white hover:bg-[#1a3060] disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add to audit
      </button>
    </div>
  );
}

function ExcelUploadCard({ onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = (file) => {
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) return toast.error("Upload a .xlsx file (download the template first)");
    setBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post("/hr/audit/upload-xlsx", { filename: file.name, data_b64: reader.result });
        toast.success(`Imported ${data.inserted} row${data.inserted === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`,
          { description: "Uploaded entries now appear in the audit records below." });
        onChanged && onChanged();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/hr/audit/upload-template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Projexino_Audit_Upload_Template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };
  return (
    <div className="rounded-2xl border border-dashed border-sky-300 bg-sky-50/40 p-4" data-testid="audit-upload-card">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700">
        <FileSpreadsheet size={12} /> Upload Excel sheet
      </div>
      <p className="mt-1 text-[11px] text-slate-500">
        Predefined columns: <b>Date · Description · Type (Credit/Debit) · Amount · Method · Reference</b>. Rows appear on screen instantly after upload.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="audit-upload-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-sky-600 px-4 py-2 text-xs font-bold text-white hover:bg-sky-700 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload .xlsx
        </button>
        <button onClick={downloadTemplate} data-testid="audit-template-btn"
          className="inline-flex items-center gap-1.5 rounded-full border border-sky-300 bg-white px-4 py-2 text-xs font-bold text-sky-700 hover:bg-sky-50">
          <FileDown size={12} /> Download template
        </button>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="audit-upload-input"
          onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
    </div>
  );
}

function AuditShareLinks() {
  const [shares, setShares] = useState([]);
  const [form, setForm] = useState({ title: "", from_date: "", to_date: "" });
  const [busy, setBusy] = useState(false);
  const [emailFor, setEmailFor] = useState(null); // share object

  const load = async () => {
    try { const { data } = await api.get("/hr/audit/shares"); setShares(data || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const shareUrl = (s) => `${window.location.origin}/audit-share/${s.token}`;

  const create = async () => {
    if (!form.from_date || !form.to_date) return toast.error("Select the duration — from & to dates");
    setBusy(true);
    try {
      const { data } = await api.post("/hr/audit/shares", form);
      toast.success("Audit link generated", { description: shareUrl(data) });
      setForm({ title: "", from_date: "", to_date: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    setBusy(false);
  };

  const remove = async (s) => {
    if (!window.confirm("Delete this audit link? Anyone with the link will lose access.")) return;
    await api.delete(`/hr/audit/shares/${s.id}`);
    load();
  };

  const copy = (s) => { navigator.clipboard.writeText(shareUrl(s)); toast.success("Link copied"); };

  return (
    <div className="rounded-2xl border border-violet-200 bg-white" data-testid="audit-share-section">
      <div className="rounded-t-2xl border-b border-violet-100 bg-violet-50/60 px-4 py-2.5">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700"><Link2 size={12} /> Shareable audit links</div>
        <p className="text-[11px] text-slate-500">Pick a duration → a link is generated automatically. Share by email or WhatsApp — recipients open the audit sheet online and can download the Excel.</p>
      </div>
      <div className="flex flex-wrap items-end gap-2 px-4 py-3">
        <div>
          <div className="text-[9px] font-bold uppercase text-slate-400">Title (optional)</div>
          <input data-testid="audit-share-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Q2 audit for CA" className="mt-0.5 w-44 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs focus:border-[#F97316] focus:outline-none" />
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase text-slate-400">From *</div>
          <input data-testid="audit-share-from" type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })}
            className="mt-0.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs focus:border-[#F97316] focus:outline-none" />
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase text-slate-400">To *</div>
          <input data-testid="audit-share-to" type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })}
            className="mt-0.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs focus:border-[#F97316] focus:outline-none" />
        </div>
        <button onClick={create} disabled={busy} data-testid="audit-share-create"
          className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#F97316] to-[#A855F7] px-4 py-2 text-xs font-bold text-white shadow hover:shadow-md disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />} Generate link
        </button>
      </div>
      {shares.length > 0 && (
        <div className="divide-y divide-slate-100 border-t border-slate-100">
          {shares.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5" data-testid={`audit-share-${s.id}`}>
              <div className="min-w-[180px] flex-1">
                <div className="text-xs font-bold text-[#0F2042]">{s.title}</div>
                <div className="text-[10px] text-slate-500">{s.from_date} → {s.to_date} · {s.row_count} rows · <span className="text-emerald-600">Cr {inr(s.total_credit)}</span> · <span className="text-rose-600">Dr {inr(s.total_debit)}</span></div>
                <a href={shareUrl(s)} target="_blank" rel="noreferrer" className="text-[10px] font-semibold text-violet-600 underline" data-testid={`audit-share-open-${s.id}`}>{shareUrl(s)}</a>
              </div>
              <button onClick={() => copy(s)} data-testid={`audit-share-copy-${s.id}`} title="Copy link"
                className="rounded-full border border-slate-200 p-1.5 text-slate-500 hover:border-[#0F2042]"><Copy size={12} /></button>
              <button onClick={() => setEmailFor(s)} data-testid={`audit-share-email-${s.id}`} title="Send by email"
                className="inline-flex items-center gap-1 rounded-full bg-[#0F2042] px-3 py-1.5 text-[10px] font-bold text-white hover:bg-[#1a3060]"><Mail size={11} /> Email</button>
              <WaShareButton
                testId={`audit-share-wa-${s.id}`}
                title="Share audit sheet on WhatsApp"
                getText={() => `*${s.title}*\nAudit sheet ${s.from_date} → ${s.to_date}\n\nOpen & download here:\n${shareUrl(s)}`}
                className="inline-flex items-center gap-1 rounded-full bg-[#25D366] px-3 py-1.5 text-[10px] font-bold text-white hover:bg-[#1DA851]"
                label="WhatsApp" size={11}
              />
              <button onClick={() => remove(s)} data-testid={`audit-share-del-${s.id}`} title="Delete link"
                className="rounded-full border border-rose-200 p-1.5 text-rose-400 hover:bg-rose-50"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}
      {emailFor && <ShareEmailModal share={emailFor} url={shareUrl(emailFor)} onClose={() => setEmailFor(null)} />}
    </div>
  );
}

function ShareEmailModal({ share, url, onClose }) {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const list = to.split(/[,;\s]+/).map((s) => s.trim()).filter((e) => /@/.test(e));
    if (!list.length) return toast.error("Enter recipient email");
    setBusy(true);
    try {
      await api.post("/email/send", {
        to: list,
        subject: `Audit sheet — ${share.title}`,
        body_html: `<p>Hello,</p>${note ? `<p>${note}</p>` : ""}<p>Please find the audit sheet for <b>${share.from_date} → ${share.to_date}</b>.</p><p style="margin:18px 0;"><a href="${url}" style="background:#F97316;color:#fff;padding:12px 24px;border-radius:9999px;text-decoration:none;font-weight:800;">Open audit sheet →</a></p><p style="font-size:12px;color:#64748B;">You can view the full sheet online and download it as Excel from the same page.</p>`,
      });
      toast.success("Audit link emailed");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Email failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" onClick={onClose} data-testid="audit-share-email-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-bold text-[#0F2042]">Email audit sheet</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-[#0F2042]"><X size={16} /></button>
        </div>
        <p className="mt-1 truncate text-[11px] text-slate-500">{share.title} · <a href={url} className="text-violet-600 underline" target="_blank" rel="noreferrer">{url}</a></p>
        <input data-testid="audit-email-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@email.com"
          className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#F97316] focus:outline-none" />
        <textarea data-testid="audit-email-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note…"
          className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs focus:border-[#F97316] focus:outline-none" />
        <button onClick={send} disabled={busy} data-testid="audit-email-send"
          className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[#F97316] py-2.5 text-xs font-bold text-white hover:bg-orange-600 disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send via Gmail
        </button>
      </div>
    </div>
  );
}
